import React, { useState, useEffect, useRef } from 'react';
import { Card, Spinner, Badge, Alert, Button, Modal, Container, Row, Col, Form, InputGroup, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { API_URL } from "../../utils/constant";
import axios from 'axios';
import { 
    faSync, 
    faExternalLinkAlt, 
    faEye, 
    faTag, 
    faShieldAlt, 
    faExclamationTriangle,
    faClock,
    faGlobe,
    faUserSecret,
    faCode,
    faServer
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import Pagination from '../Pagination/Pagination';
import moment from 'moment';

const ThreatIntelFeed = () => {
    // Loading state removed
    const [error, setError] = useState(null);
    const [feeds, setFeeds] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [selectedFeed, setSelectedFeed] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [targetedCountry, setTargetedCountry] = useState('');
    const [targetedCountryInput, setTargetedCountryInput] = useState('');
    const [targetedIndustry, setTargetedIndustry] = useState('');
    const [targetedIndustryInput, setTargetedIndustryInput] = useState('');
    const [expandedSummaries, setExpandedSummaries] = useState(new Set());
    
    // For debouncing search requests - separate timeouts for each input
    const searchTermTimeout = useRef(null);
    const countryTimeout = useRef(null);
    const industryTimeout = useRef(null);

    const fetchFeeds = async (searchTerm,targetedCountry,targetedIndustry) => {
        try {
            // Using the new API endpoint with axios
            const url = `${API_URL}/threat-intel-feeds`;

            // Create params object with required pagination
            const params = {
                page: currentPage,
                limit: itemsPerPage,
            };
            
            // Only add non-empty search filters
            if (searchTerm) params.search = searchTerm;
            if (targetedCountry) params.targetedCountry = targetedCountry;
            if (targetedIndustry) params.targetedIndustry = targetedIndustry;
            
            const response = await axios.get(url, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('session')}`,
                    'Content-Type': 'application/json'
                },
                params
            });

            // With axios, the response is already parsed as JSON
            const result = response.data;

            // Check if response indicates an error
            if (result.error === true) {
                throw new Error(result.userMessage || 'Failed to fetch threat intel feeds');
            }

            // Adapt to the specific API response structure
            const threatFeeds = result.data?.threatIntelFeeds || [];
            setFeeds(threatFeeds);

            // Set pagination information from the response
            setTotalItems(result.data?.totalCount || 0);

            // No longer need to extract categories, countries, or industries as we're using direct input
        } catch (err) {
            // Axios wraps errors, so we need to check for response property
            setError(err.response?.data?.userMessage || err.message);
        }
    };

    useEffect(() => {
        fetchFeeds(searchInput, targetedCountry, targetedIndustry);
    }, [currentPage, itemsPerPage, searchInput, targetedCountry, targetedIndustry]);

   
    
    // Enhanced search input handler with debounce
    const handleSearchChange = (e) => {
        const inputValue = e.target.value;
        setSearchInput(inputValue);
        
        // Clear any existing timeout for search term
        if (searchTermTimeout.current) {
            clearTimeout(searchTermTimeout.current);
        }
        
        // Set new timeout for search term
        searchTermTimeout.current = setTimeout(() => {
            setSearchTerm(inputValue);
            setCurrentPage(1);
            fetchFeeds();
            searchTermTimeout.current = null;
        }, 500);
    };
    


    // Enhanced industry input handler with debounce
    const handleIndustryChange = (e) => {
        const inputValue = e.target.value;
        setTargetedIndustryInput(inputValue);
        
        // Clear any existing timeout for industry
        if (industryTimeout.current) {
            clearTimeout(industryTimeout.current);
        }
        
        // Set new timeout for industry
        industryTimeout.current = setTimeout(() => {
            setTargetedIndustry(inputValue);
            setCurrentPage(1);
            fetchFeeds();
            industryTimeout.current = null;
        }, 500);
    };

    const handleShowDetails = (feed) => {
        setSelectedFeed(feed);
        setShowModal(true);
    };

    const handleClearFilters = () => {
        // Clear all timeouts
        if (searchTermTimeout.current) {
            clearTimeout(searchTermTimeout.current);
            searchTermTimeout.current = null;
        }
        if (countryTimeout.current) {
            clearTimeout(countryTimeout.current);
            countryTimeout.current = null;
        }
        if (industryTimeout.current) {
            clearTimeout(industryTimeout.current);
            industryTimeout.current = null;
        }
        
        // Reset all search states
        setSearchTerm('');
        setSearchInput('');
        setTargetedCountry('');
        setTargetedCountryInput('');
        setTargetedIndustry('');
        setTargetedIndustryInput('');
        setCurrentPage(1);
        
        // Fetch with cleared filters
        fetchFeeds();
    };
    
    // Debounced search function that waits 500ms after typing stops
    const debouncedSearch = (callback) => {
        // Clear any existing timeouts
        if (searchTermTimeout.current) {
            clearTimeout(searchTermTimeout.current);
            searchTermTimeout.current = null;
        }
        if (countryTimeout.current) {
            clearTimeout(countryTimeout.current);
            countryTimeout.current = null;
        }
        if (industryTimeout.current) {
            clearTimeout(industryTimeout.current);
            industryTimeout.current = null;
        }
        
        // Execute immediately for explicit actions like clear buttons
        callback();
    };

    // Sample data handler
    const handleShowSampleData = () => {
        // Sample threat analysis findings
        const sampleFindings = [
            {
                "threat_actor": "Volodymyr Tymoshchuk",
                "countries_targeted": [
                    "United States"
                ],
                "country_of_origin": "Ukraine",
                "industries_targeted": [],
                "types_of_attack": [
                    "ransomware"
                ],
                "iocs": []
            },
            {
                "threat_actor": "Unspecified",
                "countries_targeted": [
                    "China"
                ],
                "country_of_origin": "China",
                "industries_targeted": [
                    "Retail"
                ],
                "types_of_attack": [
                    "data leak"
                ],
                "iocs": []
            },
            {
                "threat_actor": "Unspecified",
                "countries_targeted": [],
                "country_of_origin": "Unspecified",
                "industries_targeted": [],
                "types_of_attack": [
                    "malware"
                ],
                "iocs": [
                    {
                        "ioc_type": "hash",
                        "ioc_value": "41f14d86bcaf8e949160ee2731802523e0c76fea87adf00ee7fe9567c3cec610",
                        "ioc_description": "SHA 256 hash for a prevalent malware file detected as Win.Worm.Bitmin-9847045-0."
                    },
                    {
                        "ioc_type": "hash",
                        "ioc_value": "85bbddc502f7b10871621fd460243fbc",
                        "ioc_description": "MD5 hash for a prevalent malware file detected as Win.Worm.Bitmin-9847045-0."
                    },
                    {
                        "ioc_type": "hash",
                        "ioc_value": "9f1f11a708d393e0a4109ae189bc64f1f3e312653dcf317a2bd406f18ffcc507",
                        "ioc_description": "SHA 256 hash for a prevalent malware file detected as Win.Worm.Coinminer::1201."
                    },
                    {
                        "ioc_type": "hash",
                        "ioc_value": "2915b3f8b703eb744fc54c81f4a9c67f",
                        "ioc_description": "MD5 hash for a prevalent malware file detected as Win.Worm.Coinminer::1201."
                    },
                    {
                        "ioc_type": "hash",
                        "ioc_value": "c67b03c0a91eaefffd2f2c79b5c26a2648b8d3c19a22cadf35453455ff08ead0",
                        "ioc_description": "SHA 256 hash for a prevalent malware file detected as PUA.Win.Dropper.Generic::1201."
                    },
                    {
                        "ioc_type": "hash",
                        "ioc_value": "8c69830a50fb85d8a794fa46643493b2",
                        "ioc_description": "MD5 hash for a prevalent malware file detected as PUA.Win.Dropper.Generic::1201."
                    }
                ]
            }
        ];

        // Create a sample feed object with our findings
        const sampleFeed = {
            title: "Sample Threat Intelligence Analysis",
            publishedAt: new Date().toISOString(),
            summary: "This is a sample threat intelligence analysis showing multiple threat actors, attack types, and indicators of compromise (IOCs).",
            threatIntelAnalysisFindings: sampleFindings,
            threatIntelAnalysisSummary: "Sample analysis showing a coordinated attack campaign including ransomware operations from Ukraine targeting the US, data leaks from China targeting retail industries, and widespread malware distribution with multiple hash indicators.",
            threatIntelAnalysisProcessingStatus: "COMPLETED",
            link: "https://example.com/sample-threat-report",
        };

        // Display the sample data in the modal
        setSelectedFeed(sampleFeed);
        setShowModal(true);
    };

    const renderDetailsModal = () => {
        if (!selectedFeed) return null;

        // Extract threat intel analysis data from findings
        const findings = selectedFeed.threatIntelAnalysisFindings || [];
        const threatActors = findings.map(finding => finding.threat_actor).filter(Boolean);
        const threatActor = threatActors.length > 0 ? threatActors[0] : '';
        
        // Get all attack types across all findings
        const attackTypes = findings.flatMap(finding => finding.types_of_attack || []).filter(Boolean);
        
        // Get all IOCs across all findings
        const iocs = findings.flatMap(finding => finding.iocs || []).filter(Boolean);
        
        // Get all industries targeted across all findings
        const industriesTargeted = [...new Set(findings.flatMap(finding => finding.industries_targeted || []).filter(Boolean))];
        
        // Get all countries targeted across all findings
        const countriesTargeted = [...new Set(findings.flatMap(finding => finding.countries_targeted || []).filter(Boolean))];
        
        // Get origin country from first finding that has it
        const countryOfOrigin = findings.find(finding => finding.country_of_origin)?.country_of_origin || '';
        
        // Get analysis summary
        const analysisSummary = selectedFeed.threatIntelAnalysisSummary || '';
        
        const threatLevelColor = getThreatLevelColor(attackTypes);
        const threatLevelIcon = getThreatLevelIcon(attackTypes);

        return (
            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                size="xl"
                centered
                className="threat-intel-modal"
            >
                <Modal.Header className="border-0 pb-0" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                    <div className="d-flex align-items-center">
                        <FontAwesomeIcon 
                            icon={threatLevelIcon} 
                            className="text-white me-3" 
                            size="2x"
                        />
                        <div>
                            <Modal.Title className="text-white mb-1">
                                {selectedFeed.title || 'Threat Intelligence Alert'}
                            </Modal.Title>
                            <div className="d-flex flex-wrap align-items-center">
                                <Badge bg="white" text="dark" className="me-2 mb-1">
                                    {attackTypes.length > 0 ? attackTypes[0] : 'Unknown Threat'}
                                </Badge>
                                <small className="text-white-50 me-3 mb-1">
                                    <FontAwesomeIcon icon={faClock} className="me-1" />
                                    {selectedFeed.publishedAt ? moment(selectedFeed.publishedAt).format('MMM DD, YYYY h:mm A') : 'N/A'}
                                </small>
                                {selectedFeed.threatIntelAnalysisProcessingStatus && (
                                    <Badge 
                                        bg={selectedFeed.threatIntelAnalysisProcessingStatus === 'COMPLETED' ? 'success' : 'warning'} 
                                        className="mb-1"
                                    >
                                        {selectedFeed.threatIntelAnalysisProcessingStatus}
                                    </Badge>
                                )}
                            </div>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {/* Summary Sections */}
                    <div className="mb-4">
                        <h5 className="fw-bold text-dark mb-3">
                            <FontAwesomeIcon icon={faShieldAlt} className="text-primary me-2" />
                            Original Summary
                        </h5>
                        <div className="bg-light p-3 rounded" style={{ borderRadius: '8px' }}>
                            <p className="mb-0 lh-base">{selectedFeed.summary || 'No summary available'}</p>
                        </div>
                    </div>
                    
                    {analysisSummary && (
                        <div className="mb-4">
                            <h5 className="fw-bold text-dark mb-3">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger me-2" />
                                Threat Analysis Summary
                            </h5>
                            <div className="bg-light p-3 rounded" style={{ borderRadius: '8px', borderLeft: '4px solid #dc3545' }}>
                                <p className="mb-0 lh-base">{analysisSummary}</p>
                            </div>
                        </div>
                    )}

                    {/* Threat Analysis Grid */}
                    <Row className="mb-4">
                        <Col md={6}>
                            <Card className="h-100 border-0 shadow-sm">
                                <Card.Body className="p-3">
                                    <h6 className="fw-bold text-dark mb-3">
                                        <FontAwesomeIcon icon={faTag} className="text-warning me-2" />
                                        Attack Types
                                    </h6>
                                    {attackTypes.length > 0 ? (
                                        <div className="d-flex flex-wrap gap-1">
                                            {attackTypes.map((type, index) => (
                                                <Badge key={index} bg={threatLevelColor} className="px-2 py-1">
                                                    {type}
                                                </Badge>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-muted mb-0">Not specified</p>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card className="h-100 border-0 shadow-sm">
                                <Card.Body className="p-3">
                                    <h6 className="fw-bold text-dark mb-3">
                                        <FontAwesomeIcon icon={faUserSecret} className="text-danger me-2" />
                                        Threat Actor{threatActors.length > 1 ? 's' : ''}
                                    </h6>
                                    {threatActors.length > 0 ? (
                                        <div className="d-flex flex-wrap gap-1">
                                            {threatActors.map((actor, index) => (
                                                <Badge key={index} bg="danger" className="px-3 py-2 me-1 mb-1">
                                                    {actor || ''}
                                                </Badge>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-muted mb-0"></p>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Industries and Countries */}
                    {(industriesTargeted.length > 0 || countriesTargeted.length > 0 || countryOfOrigin) && (
                        <Row className="mb-4">
                            {industriesTargeted.length > 0 && (
                                <Col md={6}>
                                    <Card className="h-100 border-0 shadow-sm">
                                        <Card.Body className="p-3">
                                            <h6 className="fw-bold text-dark mb-3">
                                                <FontAwesomeIcon icon={faGlobe} className="text-info me-2" />
                                                Industries Targeted
                                            </h6>
                                            <div className="d-flex flex-wrap gap-1">
                                                {industriesTargeted.map((industry, index) => (
                                                    <Badge key={index} bg="info" className="px-2 py-1">
                                                        {industry}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            )}
                            {(countriesTargeted.length > 0 || countryOfOrigin) && (
                                <Col md={6}>
                                    <Card className="h-100 border-0 shadow-sm">
                                        <Card.Body className="p-3">
                                            <h6 className="fw-bold text-dark mb-3">
                                                <FontAwesomeIcon icon={faGlobe} className="text-success me-2" />
                                                Geographic Information
                                            </h6>
                                            {countryOfOrigin && (
                                                <div className="mb-2">
                                                    <small className="text-muted">Origin:</small>
                                                    <Badge bg="success" className="ms-2 px-2 py-1">
                                                        {countryOfOrigin}
                                                    </Badge>
                                                </div>
                                            )}
                                            {countriesTargeted.length > 0 && (
                                                <div>
                                                    <small className="text-muted">Targeted:</small>
                                                    <div className="d-flex flex-wrap gap-1 mt-1">
                                                        {countriesTargeted.map((country, index) => (
                                                            <Badge key={index} bg="outline-success" className="px-2 py-1">
                                                                {country}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>
                            )}
                        </Row>
                    )}

                    {/* Source URL */}
                    {selectedFeed.link && (
                        <div className="mb-4">
                            <h6 className="fw-bold text-dark mb-3">
                                <FontAwesomeIcon icon={faExternalLinkAlt} className="text-primary me-2" />
                                Source Information
                            </h6>
                            <div className="bg-light p-3 rounded d-flex align-items-center justify-content-between">
                                <div className="flex-grow-1 me-3">
                                    <small className="text-muted d-block">Source URL:</small>
                                    <a
                                        href={selectedFeed.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary text-decoration-none"
                                    >
                                        {selectedFeed.link}
                                    </a>
                                </div>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    href={selectedFeed.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" />
                                    Visit
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Detailed Findings Section - only show if we have multiple findings */}
                    {findings.length > 1 && (
                        <div className="mb-4">
                            <h5 className="fw-bold text-dark mb-3">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning me-2" />
                                Detailed Findings ({findings.length})
                            </h5>
                            {findings.map((finding, findingIndex) => (
                                <Card key={findingIndex} className="mb-3 border-0 shadow-sm">
                                    <Card.Header className="bg-light border-0">
                                        <h6 className="mb-0 fw-bold">
                                            {finding.threat_actor ? 
                                                `Finding ${findingIndex + 1}: ${finding.threat_actor}` : 
                                                `Finding ${findingIndex + 1}`}
                                        </h6>
                                    </Card.Header>
                                    <Card.Body className="p-3">
                                        <Row>
                                            {finding.country_of_origin && (
                                                <Col md={4} className="mb-3">
                                                    <small className="text-muted d-block">Origin:</small>
                                                    <Badge bg="secondary" className="px-2 py-1">
                                                        {finding.country_of_origin}
                                                    </Badge>
                                                </Col>
                                            )}
                                            
                                            {finding.countries_targeted?.length > 0 && (
                                                <Col md={8} className="mb-3">
                                                    <small className="text-muted d-block">Countries Targeted:</small>
                                                    <div className="d-flex flex-wrap gap-1 mt-1">
                                                        {finding.countries_targeted.map((country, idx) => (
                                                            <Badge key={idx} bg="info" className="px-2 py-1 me-1">
                                                                {country}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </Col>
                                            )}
                                            
                                            {finding.industries_targeted?.length > 0 && (
                                                <Col md={12} className="mb-3">
                                                    <small className="text-muted d-block">Industries Targeted:</small>
                                                    <div className="d-flex flex-wrap gap-1 mt-1">
                                                        {finding.industries_targeted.map((industry, idx) => (
                                                            <Badge key={idx} bg="success" className="px-2 py-1 me-1">
                                                                {industry}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </Col>
                                            )}
                                            
                                            {finding.types_of_attack?.length > 0 && (
                                                <Col md={12} className="mb-3">
                                                    <small className="text-muted d-block">Attack Types:</small>
                                                    <div className="d-flex flex-wrap gap-1 mt-1">
                                                        {finding.types_of_attack.map((attack, idx) => (
                                                            <Badge key={idx} bg="danger" className="px-2 py-1 me-1">
                                                                {attack}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </Col>
                                            )}
                                        </Row>
                                    </Card.Body>
                                </Card>
                            ))}
                        </div>
                    )}
                    
                    {/* IOCs Table */}
                    {iocs.length > 0 && (
                        <div className="mb-4">
                            <h6 className="fw-bold text-dark mb-3">
                                <FontAwesomeIcon icon={faCode} className="text-warning me-2" />
                                Indicators of Compromise ({iocs.length})
                            </h6>
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Type</th>
                                            <th>Value</th>
                                            <th>Description</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {iocs.map((ioc, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <Badge bg="secondary" className="px-2 py-1">
                                                        {ioc.ioc_type}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    <code className="text-dark">{ioc.ioc_value}</code>
                                                </td>
                                                <td className="text-muted">{ioc.ioc_description}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <Button variant="secondary" onClick={() => setShowModal(false)} className="px-4">
                        Close
                    </Button>
                    {selectedFeed.link && (
                        <Button
                            variant="primary"
                            href={selectedFeed.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-4"
                        >
                            <FontAwesomeIcon icon={faExternalLinkAlt} className="me-2" />
                            Visit Source
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        );
    };

    const getThreatLevelColor = (attackTypes) => {
        if (!attackTypes || attackTypes.length === 0) return 'secondary';
        
        const highRiskTypes = ['malware', 'exploit', 'backdoor', 'botnet'];
        const mediumRiskTypes = ['phishing', 'credential theft', 'social engineering', 'cryptojacking'];
        
        if (attackTypes.some(type => highRiskTypes.includes(type.toLowerCase()))) {
            return 'danger';
        } else if (attackTypes.some(type => mediumRiskTypes.includes(type.toLowerCase()))) {
            return 'warning';
        }
        return 'info';
    };

    const getThreatLevelIcon = (attackTypes) => {
        if (!attackTypes || attackTypes.length === 0) return faShieldAlt;
        
        const highRiskTypes = ['malware', 'exploit', 'backdoor', 'botnet'];
        const mediumRiskTypes = ['phishing', 'credential theft', 'social engineering'];
        
        if (attackTypes.some(type => highRiskTypes.includes(type.toLowerCase()))) {
            return faExclamationTriangle;
        } else if (attackTypes.some(type => mediumRiskTypes.includes(type.toLowerCase()))) {
            return faShieldAlt;
        }
        return faShieldAlt;
    };

    const toggleSummary = (feedId) => {
        setExpandedSummaries(prev => {
            const newSet = new Set(prev);
            if (newSet.has(feedId)) {
                newSet.delete(feedId);
            } else {
                newSet.add(feedId);
            }
            return newSet;
        });
    };

    const renderFeedCard = (feed, index) => {
        const attackTypes = feed.threatIntelAnalysisFindings?.flatMap(analysis => analysis.types_of_attack || []) || [];
        const threatActor = feed.threatIntelAnalysisFindings?.[0]?.threat_actor || '';
        const iocCount = feed.threatIntelAnalysisFindings?.reduce((total, analysis) => total + (analysis.iocs?.length || 0), 0) || 0;
        const threatLevelColor = getThreatLevelColor(attackTypes);
        const threatLevelIcon = getThreatLevelIcon(attackTypes);
        
        // Summary truncation logic
        const SUMMARY_LIMIT = 150;
        const feedId = feed.id || feed._id || index;
        const isExpanded = expandedSummaries.has(feedId);
        const summary = feed.summary || '';
        const shouldTruncate = summary.length > SUMMARY_LIMIT;
        const displaySummary = shouldTruncate && !isExpanded 
            ? summary.substring(0, SUMMARY_LIMIT) + '...' 
            : summary;

        return (
            <Card key={index} className="mb-4 shadow-sm border-0" style={{ borderRadius: '12px' }}>
                <Card.Body className="p-4">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="flex-grow-1">
                            <div className="d-flex align-items-center mb-2">
                                <FontAwesomeIcon 
                                    icon={threatLevelIcon} 
                                    className={`text-${threatLevelColor} me-2`}
                                    size="lg"
                                />
                                <h5 className="mb-0 fw-bold text-dark">
                                    {feed.title || 'Threat Intelligence Alert'}
                                </h5>
                            </div>
                            <p className="text-muted mb-3 lh-base">
                                {displaySummary}
                                {shouldTruncate && (
                                    <button
                                        onClick={() => toggleSummary(feedId)}
                                        className="btn btn-link p-0 ms-2 text-primary text-decoration-none"
                                        style={{ fontSize: '0.9rem', verticalAlign: 'baseline' }}
                                    >
                                        {isExpanded ? 'Show less' : 'Show more'}
                                    </button>
                                )}
                            </p>
                        </div>
                        <div className="text-end">
                            <Badge bg={threatLevelColor} className="mb-2 px-3 py-2" style={{ fontSize: '0.8rem' }}>
                                {attackTypes.length > 0 ? attackTypes[0] : 'Unknown Threat'}
                            </Badge>
                            <div className="text-muted small">
                                <FontAwesomeIcon icon={faClock} className="me-1" />
                                {feed.publishedAt ? moment(feed.publishedAt).format('MMM DD, YYYY') : 'N/A'}
                            </div>
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-6">
                            {threatActor && (
                                <div className="d-flex align-items-center mb-2">
                                    <FontAwesomeIcon icon={faUserSecret} className="text-primary me-2" />
                                    <span className="fw-semibold">Threat Actor:</span>
                                    <Badge bg="outline-primary" className="ms-2">{threatActor}</Badge>
                                </div>
                            )}
                        </div>
                        <div className="col-md-6">
                            {iocCount > 0 && (
                                <div className="d-flex align-items-center mb-2">
                                    <FontAwesomeIcon icon={faCode} className="text-info me-2" />
                                    <span className="fw-semibold">IOCs:</span>
                                    <Badge bg="info" className="ms-2">{iocCount}</Badge>
                                </div>
                            )}
                        </div>
                    </div>

                    {attackTypes.length > 1 && (
                        <div className="mb-3">
                            <div className="d-flex align-items-center mb-2">
                                <FontAwesomeIcon icon={faTag} className="text-secondary me-2" />
                                <span className="fw-semibold">Attack Types:</span>
                            </div>
                            <div className="d-flex flex-wrap gap-1">
                                {attackTypes.slice(0, 3).map((type, idx) => (
                                    <Badge key={idx} bg="light" text="dark" className="px-2 py-1">
                                        {type}
                                    </Badge>
                                ))}
                                {attackTypes.length > 3 && (
                                    <Badge bg="secondary" className="px-2 py-1">
                                        +{attackTypes.length - 3} more
                                    </Badge>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex gap-2">
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleShowDetails(feed)}
                                className="px-3"
                            >
                                <FontAwesomeIcon icon={faEye} className="me-1" />
                                View Details
                            </Button>
                            {feed.link && (
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    href={feed.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-3"
                                >
                                    <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" />
                                    Source
                                </Button>
                            )}
                        </div>
                        <div className="text-muted small">
                            <FontAwesomeIcon icon={faServer} className="me-1" />
                            Analysis Complete
                        </div>
                    </div>
                </Card.Body>
            </Card>
        );
    };

    const renderFeedsGrid = () => {
        if (!feeds || feeds.length === 0) {
            return (
                <div className="text-center py-5">
                    <div className="mb-4">
                        <FontAwesomeIcon 
                            icon={faShieldAlt} 
                            size="4x" 
                            className="text-muted mb-3"
                        />
                    </div>
                    <h4 className="text-muted mb-2">No Threat Intelligence Feeds Found</h4>
                    <p className="text-muted">No feeds matching your current filters. Try adjusting your search criteria.</p>
                </div>
            );
        }

        return (
            <div className="feeds-grid">
                {feeds?.map((feed, index) => renderFeedCard(feed, index))}
            </div>
        );
    };

    return (
        <Container fluid>
            {/* Header Section */}
            <div className="block-header">
                <div className="row clearfix">
                    <div className="col-md-6 col-sm-12">
                        <h1 className="welcome-text">Threat Intelligence</h1>
                        <nav aria-label="breadcrumb" className="mt-1">
                            <ol className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to="/" className="welcome-textSmall">Home</Link>
                                </li>
                                <li className="breadcrumb-item active" aria-current="page" style={{ color: "white" }}>
                                    Threat Intelligence
                                </li>
                            </ol>
                            <br />
                        </nav>
                    </div>
                    <div className="col-md-6 col-sm-12 text-right hidden-xs d-flex justify-content-end">
                        <button
                            variant="success"
                            onClick={handleShowSampleData}
                            className="btn btn-sm btn-primary mr-2"
                            style={{height:'48px'}}
                        >
                            <FontAwesomeIcon icon={faEye} className="me-2" />
                            View Sample Analysis
                        </button>
                        <button
                            variant="primary"
                            style={{height:'48px'}}
                            onClick={fetchFeeds}
                            className="btn btn-sm btn-primary mr-1"
                        >
                        <FontAwesomeIcon icon={faSync} className="me-2" />
                        Refresh
                        </button>
                    </div>
                </div>
            </div>

            {/* Search and Filter Section */}
            <div className="row clearfix">
                <div className="col-lg-12 col-md-12">
                    <div className="card card_tableBox">
                        <div className="header">
                            <div className="body">
                                <div className="row">
                                    {/* Search term input */}
                                    <div className="col-sm-4 mb-2">
                                        <div className="position-relative">
                                            <Form.Control
                                                className="form-control form-control-sm ml-0 my-1"
                                                type="text"
                                                placeholder="Search... (e.g. ransom)"
                                                value={searchInput}
                                                onChange={(event)=>{
                                                    setSearchInput(event.target.value);
                                                }}
                                                aria-label="Search"
                                            />
                                        </div>
                                    </div>
                                    
                                    {/* Targeted Country filter */}
                                    <div className="col-sm-4 mb-2">
                                        <div className="position-relative">
                                            <Form.Control
                                                type="text"
                                                placeholder="Targeted Country (e.g. china)"
                                                value={targetedCountry}
                                                onChange={(event)=>{
                                                    setTargetedCountry(event.target.value)
                                                }}
                                                className="form-control form-control-sm"
                                            />
                                        </div>
                                    </div>
                                    
                                    {/* Targeted Industry filter */}
                                    <div className="col-sm-4 mb-2">
                                        <div className="position-relative">
                                            <Form.Control
                                                type="text"
                                                placeholder="Targeted Industry (e.g. government)"
                                                value={targetedIndustry}
                                                onChange={(event)=>{
                                                        setTargetedIndustry(event.target.value)
                                                }}
                                                className="form-control form-control-sm"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>            {/* Error State */}
            {error && (
                <Alert variant="danger" className="mb-4" style={{ borderRadius: '12px' }}>
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    <strong>Error:</strong> {error}
                </Alert>
            )}

            {/* Feeds Grid */}
            {!error && (
                <>
                    {renderFeedsGrid()}
                    
                    {/* Pagination */}
                    {totalItems > 0 && (
                        <div className="d-flex justify-content-between align-content-center">
                            <div className="dataTables_paginate">
                                <Pagination
                                    className="pagination-bar"
                                    currentPage={currentPage}
                                    totalCount={totalItems}
                                    pageSize={itemsPerPage}
                                    onPageChange={(page) => {
                                        setCurrentPage(page);
                                    }}
                                />
                            </div>
                            {totalItems > 10 && (
                                <select
                                    value={itemsPerPage}
                                    onChange={(e) => {
                                        setItemsPerPage(parseInt(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                    className="form-control limitSelectBox"
                                >
                                    <option value={10}>10 per page</option>
                                    <option value={25}>25 per page</option>
                                    <option value={50}>50 per page</option>
                                    <option value={100}>100 per page</option>
                                </select>
                            )}
                        </div>
                    )}
                </>
            )}

            {renderDetailsModal()}
        </Container>
    );
};

export default ThreatIntelFeed;
